import React, {FC, memo, useCallback, useEffect, useState, useRef, useMemo} from "react";
import {usePageTitle} from "../../hooks/usePageTitle";
import {SearchResult} from "./result/searchResult";
import {NavigationProps} from "../../utils/navigationProps";
import {useDispatch, useSelector} from "react-redux";
import {RootState} from "../../store/rootReducer";
import {searchActions, CardStateOption} from "./search.slice";
import {cardsActions} from "../cards.slice";
import {uiActions} from "../../ui/ui.slice"; // Import uiActions
import {CardState} from "../../common";
import styled from "styled-components";
import {ThemeProps} from "../../styles/theme";
import {styleConstants} from "../../styles/styleConstants";
import {SideMenu} from "../../review/sideMenu/sideMenu";
import { useNavigate, useLocation } from "@reach/router";
import { useConfirmDialog } from "../../components/ConfirmDialogContext";

interface SearchComponentProps extends NavigationProps {
    children?: React.ReactNode;
}

const Root = styled.div`
    height: 100vh;
    display: flex;
    flex-direction: column;
    position: relative;
`;

const ActionBar = styled.div<ThemeProps>`
    background-color: ${props => props.theme.secondaryBackground};
    border: ${props => props.theme.secondaryBorder} 1px solid;
    position: relative;
    height: ${styleConstants.actionBarHeight};
    width: 100%;
    display: flex;
    align-items: center;
    padding: 0 10px;
`;

const Icon = styled.svg<ThemeProps>`
    height: ${styleConstants.actionBarIconSize};
    width: ${styleConstants.actionBarIconSize};
    fill: ${props => props.theme.secondaryIcon};
    cursor: pointer;
`;

const SearchContent = styled.div.attrs<{ $isSelectionMode?: boolean }>(props => ({
    // Отфильтровываем пропы, которые не должны попадать в DOM
    style: {
        paddingBottom: props.$isSelectionMode === true ? '80px' : '0'
    }
}))<ThemeProps & { $isSelectionMode?: boolean }>`
    width: 90%;
    max-width: 800px;
    margin: 20px auto;
    display: flex;
    flex-direction: column;
    flex-grow: 1;
`;

const SearchText = styled.input<ThemeProps>`
    padding: 10px;
    margin-bottom: 10px;
    border-radius: 4px;
    border: ${props => props.theme.inputBorder} 1px solid;
    background-color: ${props => props.theme.inputBackground};
    color: ${props => props.theme.inputText};
    font-size: 1em;
    
    &:focus {
        outline: none;
        border-color: ${props => props.theme.accentColor};
    }
`;

const StateFilter = styled.select<ThemeProps>`
    padding: 10px;
    margin-bottom: 20px;
    border-radius: 4px;
    border: ${props => props.theme.inputBorder} 1px solid;
    background-color: ${props => props.theme.inputBackground};
    color: ${props => props.theme.inputText};
    font-size: 1em;
    
    &:focus {
        outline: none;
        border-color: ${props => props.theme.accentColor};
    }
`;

const FiltersRow = styled.div`
    display: flex;
    flex-direction: row;
    gap: 10px;
    margin-bottom: 20px;
    width: 100%;
    box-sizing: border-box;
`;

const TagFilter = styled(SearchText)`
    margin-bottom: 0;
    flex: 1;
    min-width: 0; /* Allows flex item to shrink below content size */
`;

const StateSelect = styled(StateFilter)`
    margin-bottom: 0;
    width: 120px;
    min-width: 120px;
`;

const MarkedToggle = styled.button<ThemeProps & { $active: boolean }>`
    padding: 0 12px;
    border-radius: 4px;
    border: ${props => props.$active ? props.theme.accentColor : props.theme.inputBorder} 1px solid;
    background-color: ${props => props.$active ? props.theme.accentColor : props.theme.inputBackground};
    color: ${props => props.$active ? (props.theme.buttonText || 'white') : props.theme.inputText};
    font-size: 1em;
    white-space: nowrap;
    cursor: pointer;
`;

const SelectionBar = styled.div<ThemeProps>`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    padding: 12px 16px;
    background-color: ${props => props.theme.secondaryBackground};
    border-radius: 8px 8px 0 0;
    border: ${props => props.theme.primaryBorder} 1px solid;
    border-bottom: none;
    box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.15);
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 100;
    max-width: 100%;
`;

const SelectedCount = styled.div`
    font-weight: 500;
    color: ${props => props.theme.primaryForeground};
`;

const SelectionActions = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
`;

const ActionButton = styled.button<ThemeProps>`
    padding: 8px 12px;
    border-radius: 6px;
    border: none;
    background-color: ${props => props.theme.accentColor};
    color: ${props => props.theme.buttonText || 'white'};
    cursor: pointer;
    font-size: 0.9em;
    transition: all 0.2s ease;
    display: flex;
    justify-content: center;
    align-items: center;
    white-space: nowrap;
    
    &:hover {
        opacity: 0.9;
        transform: translateY(-1px);
    }
    
    &:active {
        transform: translateY(1px);
    }
    
    &.cancel {
        background-color: transparent;
        color: ${props => props.theme.primaryForeground};
        border: none;
        font-size: 1.2em;
        padding: 8px;
    }
    
    &.delete {
        background-color: ${props => props.theme.dangerColor};
    }
    
    &.select-all {
        background-color: ${props => props.theme.successColor};
    }
`;

const ActionsMenuButton = styled(ActionButton)`
    background-color: ${props => props.theme.accentColor};
    position: relative;
`;

const ActionsDropdown = styled.div<ThemeProps>`
    position: fixed;
    bottom: 60px;
    left: 0;
    right: 0;
    width: 100%;
    background-color: ${props => props.theme.secondaryBackground};
    border: ${props => props.theme.primaryBorder} 1px solid;
    border-radius: 12px 12px 0 0;
    box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.2);
    z-index: 1000;
    overflow: hidden;
    
    @media (min-width: 601px) {
        width: 400px;
        max-width: 90%;
        left: 50%;
        transform: translateX(-50%);
        border-radius: 12px;
        box-shadow: 0 0 15px rgba(0, 0, 0, 0.2);
    }
`;

const ActionOption = styled.div<ThemeProps>`
    padding: 16px;
    cursor: pointer;
    color: ${props => props.theme.primaryForeground};
    transition: background-color 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    
    &:hover {
        background-color: ${props => props.theme.hoverBackground};
    }
    
    &:not(:last-child) {
        border-bottom: ${props => props.theme.primaryBorder} 1px solid;
    }
    
    &.delete {
        color: ${props => props.theme.dangerColor};
    }
`;

const Overlay = styled.div<ThemeProps>`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: ${props => props.theme.overlayBackground};
    z-index: 105;
`;

const SearchComponent: FC<SearchComponentProps> = (_props) => {
    const { isSelectionMode, selectedIds, text, tag, cardState, markedOnly } = useSelector((state: RootState) => state.search);

    const pageTitle = useMemo(() => {
        if (text) return `Search: ${text} · Memkeep`;
        if (tag) return `#${tag} · Memkeep`;
        if (markedOnly) return 'Marked · Memkeep';
        return 'Search · Memkeep';
    }, [text, tag, markedOnly]);
    usePageTitle(pageTitle);

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();

    const [isActionsMenuOpen, setIsActionsMenuOpen] = useState(false);
    const actionsMenuRef = useRef<HTMLButtonElement>(null);
    
    const { openConfirmDialog } = useConfirmDialog();

    const closeActionsMenu = useCallback(() => {
        setIsActionsMenuOpen(false);
    }, []);

    const toggleActionsMenu = useCallback(() => {
        setIsActionsMenuOpen(prev => !prev);
    }, []);
    
    const handleDropdownClick = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
    }, []);
    
    const handleMoveToKnownClick = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
        
        openConfirmDialog(
            'Move to Known',
            `Are you sure you want to move ${selectedIds.length} selected cards to "Known" state?`,
            () => {
                selectedIds.forEach(id => {
                    dispatch(cardsActions.moveToState({ cardId: id, state: CardState.Known }));
                });
                // Выход из режима выбора после выполнения действия
                dispatch(searchActions.clearSelection());
                closeActionsMenu();
            }
        );
    }, [selectedIds, dispatch, openConfirmDialog, closeActionsMenu]);
    
    const handleMoveToFutureClick = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
        
        openConfirmDialog(
            'Move to Future',
            `Are you sure you want to move ${selectedIds.length} selected cards to "Future" state?`,
            () => {
                selectedIds.forEach(id => {
                    dispatch(cardsActions.moveToState({ cardId: id, state: CardState.Future }));
                });
                dispatch(searchActions.clearSelection());
                closeActionsMenu();
            }
        );
    }, [selectedIds, dispatch, openConfirmDialog, closeActionsMenu]);
    
    const handleMoveToMemorizerClick = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
        
        openConfirmDialog(
            'Move to Memorizer',
            `Are you sure you want to move ${selectedIds.length} selected cards to "Memorizer" state?`,
            () => {
                selectedIds.forEach(id => {
                    dispatch(cardsActions.moveToState({ cardId: id, state: CardState.Memorizer }));
                });
                dispatch(searchActions.clearSelection());
                closeActionsMenu();
            }
        );
    }, [selectedIds, dispatch, openConfirmDialog, closeActionsMenu]);
    
    const handleUnmarkClick = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();

        openConfirmDialog(
            'Remove Marks',
            `Remove every mark (card and subcards) from ${selectedIds.length} selected cards?`,
            () => {
                dispatch(cardsActions.unmarkCards(selectedIds));
                dispatch(searchActions.clearSelection());
                closeActionsMenu();
            }
        );
    }, [selectedIds, dispatch, openConfirmDialog, closeActionsMenu]);

    const handleSelectAllClick = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();

        dispatch(searchActions.selectAll());        
        closeActionsMenu();
    }, [dispatch, closeActionsMenu]);
    
    const handleDeleteClick = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
        
        openConfirmDialog(
            'Delete Selected Cards',
            `Are you sure you want to delete ${selectedIds.length} selected cards?`,
            () => {
                selectedIds.forEach(id => {
                    dispatch(cardsActions.deleteCard(id));
                });
                dispatch(searchActions.clearSelection());
                closeActionsMenu();
            }
        );
    }, [selectedIds, dispatch, openConfirmDialog, closeActionsMenu]);
    
    const updateUrlParams = useCallback((newText: string, newTag: string, newState: CardStateOption, newMarkedOnly: boolean) => {
        const params = new URLSearchParams();
        if (newText) params.set('q', newText);
        if (newTag) params.set('tag', newTag);
        if (newState !== CardStateOption.All) params.set('state', newState);
        if (newMarkedOnly) params.set('marked', '1');
        
        const search = params.toString();
        const newUrl = `${location.pathname}${search ? '?' + search : ''}`;
        navigate(newUrl, { replace: true });
    }, [navigate, location.pathname]);

    const searchTextChangedHandler = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const newText = event.target.value;
        dispatch(searchActions.setText(newText));
        updateUrlParams(newText, tag, cardState, markedOnly);
    }, [dispatch, tag, cardState, markedOnly, updateUrlParams]);

    const searchTagChangedHandler = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const newTag = event.target.value;
        dispatch(searchActions.setTag(newTag));
        updateUrlParams(text, newTag, cardState, markedOnly);
    }, [dispatch, text, cardState, markedOnly, updateUrlParams]);
    
    const cardStateChangedHandler = useCallback((event: React.ChangeEvent<HTMLSelectElement>) => {
        const newState = event.target.value as CardStateOption;
        dispatch(searchActions.setCardState(newState));
        updateUrlParams(text, tag, newState, markedOnly);
    }, [dispatch, text, tag, markedOnly, updateUrlParams]);

    const markedOnlyToggledHandler = useCallback(() => {
        const newMarkedOnly = !markedOnly;
        dispatch(searchActions.setMarkedOnly(newMarkedOnly));
        updateUrlParams(text, tag, cardState, newMarkedOnly);
    }, [dispatch, text, tag, cardState, markedOnly, updateUrlParams]);
    
    const openMenuHandler = useCallback(() => {
        dispatch(uiActions.openSideMenu());
    }, [dispatch]);
    
    const cancelSelectionHandler = useCallback(() => {
        dispatch(searchActions.clearSelection());
    }, [dispatch]);

    useEffect(() => {
        // Initialize state from URL parameters
        const params = new URLSearchParams(location.search);
        const urlTextParam = params.get('q');
        const urlText = urlTextParam !== null ? urlTextParam : '';
        const urlTag = params.get('tag');
        const urlTagValue = urlTag !== null ? urlTag : '';
        const urlStateParam = params.get('state');
        const urlState = urlStateParam !== null ? (urlStateParam as CardStateOption) : CardStateOption.All;
        const urlMarkedOnly = params.get('marked') === '1';

        // When no parameters in URL, trigger search with empty text and All state
        if (!params.has('q') && !params.has('tag') && !params.has('state') && !params.has('marked')) {
            dispatch(searchActions.setText(''));
            dispatch(searchActions.setTag(''));
            dispatch(searchActions.setCardState(CardStateOption.All));
            dispatch(searchActions.setMarkedOnly(false));
        } else {
            if (urlText !== text) {
                dispatch(searchActions.setText(urlText));
            }
            if (urlTagValue !== tag) {
                dispatch(searchActions.setTag(urlTagValue));
            }
            if (urlState !== cardState) {
                dispatch(searchActions.setCardState(urlState));
            }
            if (urlMarkedOnly !== markedOnly) {
                dispatch(searchActions.setMarkedOnly(urlMarkedOnly));
            }
        }
    }, [location.search, dispatch, text, tag, cardState, markedOnly]);

    return (
        <Root>
            <ActionBar>
                <Icon xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" onClick={openMenuHandler}>
                    <path d="M0 0h24v24H0z" fill="none"/>
                    <path d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z"/>
                </Icon>
            </ActionBar>
            <SearchContent $isSelectionMode={isSelectionMode}>
                <SearchText
                    type="text"
                    data-testid="search-input"
                    value={text}
                    onChange={searchTextChangedHandler}
                    placeholder="Search cards..."
                />
                
                <FiltersRow>
                    <TagFilter
                        type="text"
                        data-testid="search-tag"
                        value={tag}
                        onChange={searchTagChangedHandler}
                        placeholder="Filter by tag..."
                    />

                    <StateSelect className="search-card-state" data-testid="search-state" value={cardState} onChange={cardStateChangedHandler}>
                        <option value={CardStateOption.All}>All</option>
                        <option value={CardStateOption.Known}>Known</option>
                        <option value={CardStateOption.Future}>Future</option>
                        <option value={CardStateOption.Memorizer}>Memorizer</option>
                    </StateSelect>

                    <MarkedToggle
                        type="button"
                        data-testid="search-marked"
                        $active={markedOnly}
                        aria-pressed={markedOnly}
                        title="Only cards marked for regeneration"
                        onClick={markedOnlyToggledHandler}
                    >
                        🚩 Marked
                    </MarkedToggle>
                </FiltersRow>

                <SearchResult>
                </SearchResult>
            </SearchContent>
            
            {isSelectionMode && (
                <SelectionBar>
                    <SelectedCount>
                        {selectedIds.length} {selectedIds.length === 1 ? 'item' : 'items'} selected
                    </SelectedCount>
                    <SelectionActions>
                        <ActionsMenuButton 
                            data-testid="search-actions"
                            ref={actionsMenuRef}
                            onClick={toggleActionsMenu}
                        >
                            Actions
                        </ActionsMenuButton>
                        
                        {isActionsMenuOpen && (
                            <>
                                <Overlay />
                                <ActionsDropdown 
                                    className="actions-dropdown"
                                    onClick={(e) => {
                                        handleDropdownClick(e);
                                    }}
                                >
                                    <ActionOption 
                                        onClick={(e) => {
                                            handleMoveToKnownClick(e);
                                        }}
                                    >
                                        <span>Known</span>
                                    </ActionOption>
                                    <ActionOption 
                                        onClick={(e) => {
                                            handleMoveToFutureClick(e);
                                        }}
                                    >
                                        <span>Future</span>
                                    </ActionOption>
                                    <ActionOption 
                                        onClick={(e) => {
                                            handleMoveToMemorizerClick(e);
                                        }}
                                    >
                                        <span>Memorizer</span>
                                    </ActionOption>
                                    <ActionOption
                                        data-testid="search-unmark"
                                        onClick={(e) => {
                                            handleUnmarkClick(e);
                                        }}
                                    >
                                        <span>Remove marks</span>
                                    </ActionOption>
                                    <ActionOption 
                                        onClick={(e) => {
                                            handleSelectAllClick(e);
                                        }}
                                    >
                                        <span>Select All</span>
                                    </ActionOption>
                                    <ActionOption 
                                        className="delete"
                                        onClick={(e) => {
                                            handleDeleteClick(e);
                                        }}
                                    >
                                        <span>Delete</span>
                                    </ActionOption>
                                </ActionsDropdown>
                            </>
                        )}
                    </SelectionActions>
                    <ActionButton 
                        className="cancel"
                        onClick={cancelSelectionHandler}
                    >
                        ✕
                    </ActionButton>
                </SelectionBar>
            )}
            
            <SideMenu />
        </Root>
    );
}

export const Search = memo(SearchComponent);